import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from "../../firebase";
import { collection, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import './GroceryProductList.css';

function GroceryProductList() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  // This is no longer the primary source for the quantity display but can be kept for other purposes
  const [stockMap, setStockMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);

    const productsUnsubscribe = onSnapshot(collection(db, 'groceryProducts'), (snapshot) => {
      const productsList = snapshot.docs
        .map(doc => {
          const data = doc.data();
          if (!data || !data.name) return null;
          return { id: doc.id, ...data };
        })
        .filter(product => product !== null);

      setProducts(productsList);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching products: ", err);
      setError('Failed to load products.');
      setLoading(false);
    });

    // This listener can remain if you need GRN data for other features
    const grnsUnsubscribe = onSnapshot(collection(db, 'grocery grns'), (snapshot) => {
      const newStockMap = {};
      snapshot.docs.forEach(doc => {
        const grn = doc.data();
        if (grn?.items && Array.isArray(grn.items)) {
          grn.items.forEach(item => {
            if (item?.productId && item?.unit) {
              const variantKey = `${item.productId}-${item.unit}`;
              newStockMap[variantKey] = (newStockMap[variantKey] || 0) + (item.quantityReceived || 0);
            }
          });
        }
      });
      setStockMap(newStockMap);
    }, (err) => {
      console.error("Error fetching GRNs: ", err);
      // This is not a critical error for the main product display anymore
      // setError('Failed to load product stock levels.');
    });

    return () => {
      productsUnsubscribe();
      grnsUnsubscribe();
    };
  }, []);

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await deleteDoc(doc(db, 'groceryProducts', id));
        alert('Product deleted successfully!');
      } catch (err) {
        console.error("Error deleting product: ", err);
        alert('Failed to delete product.');
      }
    }
  };

  const handleEdit = (productId) => {
    if (!productId) {
        alert("Error: Cannot edit product. Its ID is missing.");
        return;
    }
    navigate(`/groceryproduct/edit/${productId}`);
  };

  if (loading) return <div className="centered">Loading products...</div>;
  if (error) return <div className="centered error-text">{error}</div>;

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>Grocery Products</h2>
        <button className="add-button" onClick={() => navigate('/groceryproduct/add')}>
          <FaPlus style={{ marginRight: '8px' }} />
          Add Product
        </button>
      </div>
      <div className="table-wrapper">
        <table className="products-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Category</th>
              <th>Unit</th>
              <th>Price</th>
              <th>Discount</th>
              {/* MODIFICATION 1: Changed table header */}
              <th>Quantity</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.length > 0 ? (
              products.map(product => (
                <React.Fragment key={product.id}>
                  {product.variants && product.variants.length > 0 ? (
                    product.variants.map((variant, variantIndex) => {
                      const variantUnit = variant?.unit ?? 'N/A';
                      // Use the product's variant key if needed elsewhere, but get quantity directly
                      const variantKey = `${product.id}-${variantUnit}`;

                      // --- MODIFICATION 2: Get quantity directly from the variant ---
                      const quantity = variant?.quantity ?? 0;
                      
                      const price = variant?.price ?? 0;
                      const discount = variant?.discount ?? 0;

                      return (
                        <tr key={variantKey}>
                          {variantIndex === 0 && (
                            <>
                              <td rowSpan={product.variants.length}>
                                <img
                                  src={product.images_base64?.[0] ?? 'https://via.placeholder.com/100'}
                                  alt={product.name}
                                  className="product-image"
                                />
                              </td>
                              <td rowSpan={product.variants.length}>{product.name ?? 'Unnamed Product'}</td>
                              <td rowSpan={product.variants.length}>{product.category ?? 'N/A'}</td>
                            </>
                          )}
                          
                          <td>{variantUnit}</td>
                          <td>₹{price.toFixed(2)}</td>
                          <td>{discount > 0 ? `${discount}%` : 'N/A'}</td>
                          
                          {/* MODIFICATION 3: Display the quantity from the variant */}
                          <td className={quantity <= 10 ? 'low-stock' : ''}>{quantity}</td>

                          {variantIndex === 0 && (
                            <td rowSpan={product.variants.length}>
                              <button className="action-button edit-button" onClick={() => handleEdit(product.id)}>
                                <FaEdit />
                              </button>
                              <button className="action-button delete-button" onClick={() => handleDelete(product.id, product.name)}>
                                <FaTrash />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  ) : (
                    // Fallback for products with no variants
                    <tr>
                      <td>
                        <img
                          src={product.images_base64?.[0] ?? 'https://via.placeholder.com/100'}
                          alt={product.name}
                          className="product-image"
                        />
                      </td>
                      <td>{product.name ?? 'Unnamed Product'}</td>
                      <td>{product.category ?? 'N/A'}</td>
                      <td colSpan="4">This product has no variants.</td>
                      <td>
                        <button className="action-button edit-button" onClick={() => handleEdit(product.id)}>
                            <FaEdit />
                        </button>
                        <button className="action-button delete-button" onClick={() => handleDelete(product.id, product.name)}>
                            <FaTrash />
                        </button>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr><td colSpan="8" className="centered">No products found. Add one to get started!</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default GroceryProductList;